import {useBackDrop, useStores} from "../../Hooks";
import './fastTrack.scss';
import frontlineLogoBlue from '../../assets/img/frontline-logo-blue.png';
import frontlineLogoBlueNoShade from '../../assets/img/frontline-logo-blue-no-shade.png';
import vLogo from '../../assets/img/v-logo.png';
import {Button, IconButton, LinearProgress, TextField} from "@mui/material";
import React, {useEffect, useRef, useState} from "react";
import ConditionalComponent from "../Shared/ConditionalComponent";
import Utility from "../../Utility";
import {GeneralRequester} from "../../MobxStores/RequestApi/GeneralRequester";
import {observer} from "mobx-react";
import warningRed from "../../assets/img/warning-red.png";
import {DeleteOutlined} from "@mui/icons-material";
import {GoogleReCaptchaProvider, useGoogleReCaptcha} from "react-google-recaptcha-v3";
import sharedUtils from "../../serverClientShared/sharedUtils";
import {hotjar} from 'react-hotjar';
import Settings from "../../settings.json";
import Link from "@mui/material/Link";

const steps = {
    DETAILS: 'details',
    UPLOAD: 'upload',
    VALIDATE: 'validate'
}

const LEGAL_FAST_TRACK_EXTENSIONS = ['pxz', '3ds', 'fbx', 'gltf', 'glb', 'igs', 'iges', 'jt', 'stl', 'obj', 'mtl', 'prt', 'x_b', 'x_t', 'p_t', 'p_b', 'xmt', 'xmt_txt', 'xmt_bin', 'pdf', '3dm', 'skp', 'stp', 'step', 'stpz', 'stepz', 'stpx', 'stpxz', '3dxml'];


const inputAcceptString = LEGAL_FAST_TRACK_EXTENSIONS.map(s => `.${s},.${s.toUpperCase()}`).join(',');
const illegalEmails = ['gmail', 'yahoo', 'hotmail', 'outlook'];

const FastTrack = observer(() => {
    const {generalStore, modalStore} = useStores();

    const fileSelectorRef = useRef(null);

    const {executeRecaptcha} = useGoogleReCaptcha();

    const [selectedFile, setSelectedFile] = useState(null);
    const [step, setStep] = useState(steps.DETAILS);
    const [failedOnce, setFailedOnce] = useState(false);
    const [fileError, setFileError] = useState('');
    const [uploadProgress, setUploadProgress] = useState(0);
    const [email, setEmail] = useState('');
    const [website, setWebsite] = useState('');
    const [filledAnyInput, setFilledAnyInput] = useState(false);

    const dropRef = useRef(null);
    let isValidEmail = Utility.verifyEmail(email);
    for (const illegalEmail of illegalEmails) {
        if (email.includes(illegalEmail)) {
            isValidEmail = false;
            break;
        }
    }
    const isValidWebsite = Utility.validateUrl(website);
    const showEmailError = !isValidEmail && !!email;
    const showWebsiteError = !isValidWebsite && !!website;
    const isDisabled = !isValidEmail || !isValidWebsite || !selectedFile;

    useBackDrop(
        dropRef,
        () => {
        },
        () => {
        },
        (files) => {
            const file = files[0];
            if (validateFile(file)) {
                setSelectedFile(file);
            }
        }
    );

    useEffect(() => {
        generalStore.showHelpDesk = false;
        GeneralRequester.sendAnalyticsEvent('Fast Track - landed', 'fast-track').then();
        hotjar.initialize(Settings.hotjarId, 6);
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!!selectedFile && !!email && !!website && !filledAnyInput) {
            GeneralRequester.sendAnalyticsEvent('Fast Track - filled any input', 'fast-track').then();
        }
        setFilledAnyInput(true)
    }, [selectedFile, email, website, filledAnyInput]);


    function validateFile(file) {
        if (!file) {
            return false;
        }
        const fileExtension = file.name.split('.').pop().toLowerCase();
        if (!LEGAL_FAST_TRACK_EXTENSIONS.includes(fileExtension)) {
            setFileError(generalStore.translate('fileFormatNotSupported'));
            return false;
        }

        if (file.size > sharedUtils.FAST_TRACK_FILE_LIMIT) {
            setFileError(generalStore.translate('fileTooLarge') + '. ' + generalStore.translate('maximumFileSizeIs') + ' ' + sharedUtils.FAST_TRACK_FILE_LIMIT_MB + 'MB');
            return false;
        }
        return true;
    }

    async function uploadFile() {
        const captchaToken = await executeRecaptcha('submit');
        if (!captchaToken) {
            throw new Error('captcha');
        }
        const fileName = selectedFile.name.trim();
        console.log('fileName', fileName);

        const fileSizeInMB = selectedFile.size / 1024 / 1024;

        const websiteArr = website.split('.');
        const workspace = websiteArr[websiteArr.length - 2].toLowerCase() + sharedUtils.generateRandomStringOfNumbers(3);

        const file = new File([selectedFile], fileName, {
            type: selectedFile.type,
            lastModified: selectedFile.lastModified,
        });


        const chunks = Utility.getChunks(file, true);
        const chunksCount = chunks.length;
        const {
            uploadUrls,
            uploadId,
            fileKey
        } = await GeneralRequester.getUploadUrlFastTrack(file.name, chunksCount, captchaToken, email, workspace);

        let i = 0;
        const partsData = [];
        for (const chunk of chunks) {
            const etag = await GeneralRequester.execUploadUrlS3(uploadUrls[i], chunk);
            partsData.push({
                ETag: etag,
                PartNumber: i + 1
            });
            i++;
            const percentage = Math.ceil(100 * (i / chunksCount));
            setUploadProgress(percentage);
        }

        await GeneralRequester.finishUploadFastTrack(fileName, fileKey, uploadId, partsData, email, workspace, website, fileSizeInMB);
        setStep(steps.VALIDATE)
    }

    async function submit() {
        if (isDisabled) {
            return;
        }

        GeneralRequester.sendAnalyticsEvent('Fast Track - clicked submit', 'fast-track').then();
        if (failedOnce) {
            GeneralRequester.sendAnalyticsEvent('Fast Track - submit after fail', 'fast-track').then();
        }
        try {
            setStep(steps.UPLOAD);
            await uploadFile();
        } catch (e) {
            modalStore.showErrorMessage(generalStore.translate('submitFailed') + ', ' + generalStore.translate('pleaseContact') + ': fasttrack@frontline.io', true);
            Utility.sleep(8000).then(() => modalStore.showErrorMessage(''));
            setUploadProgress(0);
            setStep(steps.DETAILS);
            GeneralRequester.sendAnalyticsEvent('Fast Track - clicked submit failed status ' + e?.response?.status, 'fast-track').then();
            setFailedOnce(true);
        }
    }

    function renderFileSelector() {
        return (
            <>
                <div className='fast-track-step high'>
                    <div className='fast-track-drop-container' ref={dropRef}>
                        <div className='fast-track-drop-img-container'>
                            <img src={frontlineLogoBlue} alt=''/>
                        </div>

                        <ConditionalComponent if={!selectedFile}>
                            <div className='fast-track-drop-description'>
                                {generalStore.translate('drag') + ' ' + generalStore.translate('&') + ' ' + generalStore.translate('drop')}
                                <br/>
                                {generalStore.translate('OR').toUpperCase()}
                                <br/>
                                <Button
                                    variant='text'
                                    className='browser-file-button'
                                    onClick={() => {
                                        if (!fileSelectorRef || !fileSelectorRef.current) {
                                            return;
                                        }
                                        fileSelectorRef.current.click();
                                    }}
                                >
                                    {generalStore.translate('browse')}
                                </Button>
                                <div className='file-error'>
                                    <ConditionalComponent if={!!fileError}>
                                        <img src={warningRed} alt='warning red'/>
                                        {fileError}
                                    </ConditionalComponent>
                                </div>
                                <input
                                    type="file"
                                    ref={fileSelectorRef}
                                    style={{display: "none"}}
                                    accept={inputAcceptString}
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (validateFile(file)) {
                                            setSelectedFile(file);
                                        }
                                    }}
                                />

                            </div>
                        </ConditionalComponent>
                        <ConditionalComponent if={!!selectedFile}>
                            <div className='file-and-delete-container'>
                                <div className='file-holder'>
                                    {selectedFile?.name}
                                </div>
                                <IconButton onClick={() => setSelectedFile(null)}>
                                    <DeleteOutlined htmlColor='#FF1F1F' fontSize='medium'/>
                                </IconButton>
                            </div>
                        </ConditionalComponent>

                        <ul className='fast-track-drop-disclaimer'>
                            <li>{generalStore.translate('ToViewAListOfSupportedFileTypes,PleaseVisit')} <Link className='link'
                                href='https://frontlinehelpdesk.zohodesk.com/portal/en/kb/articles/fast-track-3d-cad-file-supported-formats'>{generalStore.translate('thisLink', true)}</Link>
                            </li>
                            <li>{generalStore.translate('filesMustBeSmallerThan') + ' 200 MB'}</li>
                        </ul>
                    </div>
                    <div className='fast-track-input-container'>
                        <div className='fast-track-inputs'>
                            <TextField
                                className='fast-track-input'
                                variant='outlined'
                                placeholder={generalStore.translate('workEmail') + '*'}
                                onChange={e => setEmail(e.target.value)}
                                value={email}
                                error={showEmailError}
                                helperText={showEmailError ? generalStore.translate('emailNotValid - PleaseUseWorkEmail') : ''}
                            />
                            <TextField
                                className='fast-track-input'
                                variant='outlined'
                                placeholder={generalStore.translate('companyWebsite') + '*'}
                                error={showWebsiteError}
                                helperText={showWebsiteError ? generalStore.translate('pleaseInsertYourCompanyWebsite') : ''}
                                onChange={e => setWebsite(e.target.value)}
                                value={website}
                            />
                        </div>
                        <div
                            onMouseEnter={() => {
                                if (!selectedFile && !fileError) {
                                    setFileError(generalStore.translate('pleaseAddYour') + ' CAD ' + generalStore.translate('file', true));
                                }
                            }}
                            onMouseLeave={() => setFileError('')}
                        >
                            <Button
                                className='submit-btn'
                                disabled={isDisabled}
                                onClick={() => submit()}
                            >
                                {generalStore.translate('submit')}
                            </Button>

                        </div>
                    </div>
                </div>
            </>
        )
    }

    return (
        <div className='fast-track-container'>
            {/*<LanguageMenu onBoarding={true}/>*/}
            <div className='fast-track-background-container'>

                <ConditionalComponent if={step === steps.DETAILS}>
                    {renderFileSelector()}
                </ConditionalComponent>

                <ConditionalComponent if={step === steps.UPLOAD}>
                    <div className='fast-track-step'>

                        <div className='fast-track-uploader-logo-container'>
                            <img src={frontlineLogoBlue} alt=''/>
                        </div>
                        <div className='fast-track-uploader-progress-container'>
                            <div className='fast-track-uploader-progress-text'>
                                {generalStore.translate('uploading') + '...'}
                            </div>
                            <div className='fast-track-uploader-progress-percentage'>
                                {uploadProgress + '%'}
                            </div>
                            <LinearProgress
                                className='fast-track-uploader-progress-bar'
                                value={uploadProgress}
                                variant="determinate"
                            />
                        </div>
                    </div>

                </ConditionalComponent>
                <ConditionalComponent if={step === steps.VALIDATE}>

                    <div className='fast-track-step'>

                        <div className='fast-track-done-logo-container'>
                            <img src={frontlineLogoBlueNoShade} alt=''/>
                        </div>
                        <div className='fast-track-done-message-container'>
                            <div className='v-logo-container'>
                                <img src={vLogo} alt=''/>
                            </div>
                            <div className='fast-track-done-title'>
                                {generalStore.translate('uploadedSuccessfully')}
                            </div>
                            <ul className='fast-track-done-description'>
                                <li>{generalStore.translate('pleaseCheckYourEmailForVerificationAndFurtherInstructions')}</li>
                                <li>
                                    {generalStore.translate('ifYouDidNotReceiveAnEmailPleaseContactUsAt')}
                                    &nbsp;
                                    <a
                                        href='https://helpdesk.frontline.io/en/'
                                        target='_blank'
                                        rel="noreferrer"
                                    >
                                        {generalStore.translate('supportTeam', true)}
                                    </a>{/*TODO*/}
                                </li>
                            </ul>
                        </div>
                    </div>
                </ConditionalComponent>

            </div>
        </div>
    )
});
export default function FastTrackWrapper() {
    return <GoogleReCaptchaProvider reCaptchaKey='6LcUuiYlAAAAADpgtMKehMvA2r02Wz1al4S7sygA'>
        <FastTrack/>
    </GoogleReCaptchaProvider>
}
